import React from 'react';
import Text from 'Components/Typography/Text';
import { Box } from '@igooods/ui-kit';
import styled from 'styled-components';
import MobileBusinessImg from './images/business-mobile.png';
import DesktopBusinessImg from './images/business-desk.png';
import DesktopBusinessRetinaImg from './images/business-desk@2x.png';
import BlockContainer from 'Components/landings/BlockContainer';
import { colors } from 'constants/colors';
import BlockTitle from 'Components/landings/BlockTitle';
import ThemeButton from 'Components/ThemedButton';
import { EnumTheme, EnumButtonSize, EnumGtagEvents } from 'constants/enums';
import { mediaSmDown, mediaLgDown, mediaMdDown, mediaXlDown, mediaSmUp, mediaXlUp } from 'theme/helpers/css';
import { FontSize } from 'theme';
import useRootUrlWithSubdomain from 'utils/hooks/useRootUrlWithSubdomain';
import ExternalLink from 'Components/ExternalLink';
import { URL } from 'constants/urlMaps';
import analyticsManager from 'utils/tagManager/manager';
import { GtagFrom, IBlockWithDevice } from '../types';

const Block = styled(Box)`
  position: relative;
`;

const Content = styled(Box)`
  max-width: 449px;

  ${mediaMdDown`
    max-width: 381px;
  `}

  ${mediaSmDown`
    max-width: none;
  `}
`;

const Banner = styled.div`
  position: absolute;
  width: calc(100% - 260px);
  max-width: 1170px;
  right: -190px;
  top: 50%;
  transform: translateY(-50%);

  ${mediaXlDown`
    max-width: 1020px;
    width: calc(100% - 180px);
    right: -225px;
  `}

  ${mediaLgDown`
    max-width: 536px;
    right: -138px;
  `}

  ${mediaMdDown`
    width: calc(100% - 148px);
    max-width: 550px;
    right: -172px;
  `}

  ${mediaSmDown`
    position: static;
    transform: none;
    margin-top: 40px;
    width: 112%;
    max-width: 375px;
    transform: translateX(38px);
  `}

  img,
  picture {
    width: 100%;
  }
`;

const Description = styled(Text).attrs({
  size: FontSize.m,
})`
  max-width: 368px;
  ${mediaLgDown`
    font-size: 14px;
    line-height: 22px;
  `}

  ${mediaSmDown`
    max-width: none;
  `}
`;

const Button = styled(ThemeButton).attrs({ fluid: true })`
  ${mediaSmUp`
    padding: 0 24px 2px;
  `}
`;

const BoxWrapper = styled(Box).attrs({
  mt: { xs: 9.5, sm: 20.5, lg: 62 },
})`
  width: 210px;
  ${mediaXlUp`
    margin-top: 374px;
  `}

  ${mediaSmDown`
    width: 100%;
  `}
`;

const Business: React.FC<IBlockWithDevice> = ({ device }) => {
  const { mainRoute } = useRootUrlWithSubdomain();

  const handleBtnClick = () => {
    analyticsManager.gtag(EnumGtagEvents.CLICK_CALL_TO_ACTION, {
      from: GtagFrom.BUSINESS,
      device,
    });
  };

  return (
    <BlockContainer bgColor={colors.algaeGreen}>
      <Block
        pt={{ xs: 12, sm: 14, lg: 22, xl: 30 }}
        pb={{
          xs: 15,
          lg: 24,
          xl: 30,
        }}
      >
        <Content>
          <h2>
            <BlockTitle>Работаем для&nbsp;бизнеса&nbsp;&mdash; доставляем продукты в&nbsp;офис</BlockTitle>
          </h2>
          <Box mt={{ xs: 4.5, sm: 7 }}>
            <Description>
              Можем регулярно привозить кофе, чай, молоко, овощи, фрукты и&nbsp;даже канцелярию. Оплата по&nbsp;безналу.
            </Description>
          </Box>
          <BoxWrapper>
            <ExternalLink href={`${mainRoute}${URL.business}`}>
              <Button use={EnumTheme.roundGreen} size={EnumButtonSize.large} onClick={handleBtnClick}>
                Заказать продукты
              </Button>
            </ExternalLink>
          </BoxWrapper>

          <Banner>
            <picture>
              <source media="(max-width: 1023px)" srcSet={`${DesktopBusinessImg} 2x, ${MobileBusinessImg} 1x`} />
              <source media="(min-width: 1024px)" srcSet={`${DesktopBusinessRetinaImg} 2x, ${DesktopBusinessImg} 1x`} />
              <img src={DesktopBusinessImg} alt="" data-loading="lazy" />
            </picture>
          </Banner>
        </Content>
      </Block>
    </BlockContainer>
  );
};

export default Business;
